import { useEffect, useRef, useState } from 'react';
import { SIGNATURE_HEIGHT, SIGNATURE_WIDTH, isSigned } from '@sitzmark/core';
import { Button } from '../../components/ui.js';

/**
 * A signature, drawn with a finger, a stylus or a mouse (SCOPE §11.1).
 *
 * What it produces is **path data** — `M x y L x y …` in a fixed 600 × 160
 * space, whatever size the box is drawn at — not an image. The server keeps
 * exactly that, validated so nothing but move-and-line commands can be stored,
 * and the printed waiver draws it back as a line. Pointer events rather than
 * mouse or touch events, so a finger on the counter tablet, a stylus and a
 * mouse in the back office all sign the same way.
 *
 * Signing is not the only record: the signer's printed name is typed beside
 * it, which is also the route for anyone who cannot draw one.
 */
export { SIGNATURE_HEIGHT, SIGNATURE_WIDTH };

export function SignaturePad({
  onChange,
  label = 'Sign here',
}: {
  /** The path so far, or '' once cleared. */
  onChange: (path: string) => void;
  label?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const path = useRef('');
  const drawing = useRef(false);
  const [signed, setSigned] = useState(false);
  // Something is drawn, but not enough to be a signature: say so, not nothing.
  const [short, setShort] = useState(false);

  // The canvas is drawn at the device's pixel density so the line is crisp on
  // a tablet, while every coordinate is kept in the fixed logical space.
  useEffect(() => {
    const element = canvas.current;
    if (element === null) return;
    const ratio = window.devicePixelRatio || 1;
    element.width = SIGNATURE_WIDTH * ratio;
    element.height = SIGNATURE_HEIGHT * ratio;
    const context = element.getContext('2d');
    if (context === null) return;
    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = getComputedStyle(element).color;
  }, []);

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.round(((event.clientX - box.left) * SIGNATURE_WIDTH) / box.width),
      y: Math.round(((event.clientY - box.top) * SIGNATURE_HEIGHT) / box.height),
    };
  };

  const context = () => canvas.current?.getContext('2d') ?? null;

  return (
    <div className="signature">
      <div className="signature__head">
        <span className="field__label" id="signature-label">
          {label}
        </span>
        {/* Shown once there is something to clear (§2.2-13). */}
        {signed || short ? (
          <Button
            variant="ghost"
            onClick={() => {
              const element = canvas.current;
              context()?.clearRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
              if (element !== null) element.dataset.strokes = '0';
              path.current = '';
              setSigned(false);
              setShort(false);
              onChange('');
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
      <canvas
        ref={canvas}
        className="signature__pad"
        role="img"
        aria-labelledby="signature-label"
        data-strokes="0"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drawing.current = true;
          const { x, y } = point(event);
          path.current += `${path.current === '' ? '' : ' '}M ${x} ${y}`;
          const ctx = context();
          ctx?.beginPath();
          ctx?.moveTo(x, y);
        }}
        onPointerMove={(event) => {
          if (!drawing.current) return;
          const { x, y } = point(event);
          path.current += ` L ${x} ${y}`;
          const ctx = context();
          ctx?.lineTo(x, y);
          ctx?.stroke();
        }}
        onPointerUp={(event) => {
          if (!drawing.current) return;
          drawing.current = false;
          event.currentTarget.dataset.strokes = String(
            Number(event.currentTarget.dataset.strokes ?? '0') + 1,
          );
          // A tap is a dot, not a signature: the same rule the server holds
          // (@sitzmark/core/signature) — enough ink, reaching far enough.
          const signedNow = isSigned(path.current);
          setSigned(signedNow);
          setShort(!signedNow && path.current !== '');
          onChange(signedNow ? path.current : '');
        }}
        onPointerCancel={() => {
          drawing.current = false;
        }}
      />
      <p className="field__hint">
        {signed
          ? 'Signed.'
          : short
            ? 'That is too short to be a signature — keep signing, or Clear and sign across the box.'
            : 'Draw a signature with a finger, a stylus or the mouse.'}
      </p>
    </div>
  );
}

/** A stored signature, drawn back as a line — on screen or on the printed waiver. */
export function SignatureImage({
  path,
  width,
  height,
  label,
}: {
  path: string;
  width: number | null;
  height: number | null;
  label: string;
}) {
  return (
    <svg
      className="signature__image"
      viewBox={`0 0 ${width ?? SIGNATURE_WIDTH} ${height ?? SIGNATURE_HEIGHT}`}
      role="img"
      aria-label={label}
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
